import { useCallback, useState } from "react";
import { toast } from "sonner";
import { errorMessage } from "../../../api/client";
import { money, periodApi, type Adjustment } from "../../../api/periods";
import { adminApi } from "../../../api/resources";
import { Button, TextInput } from "../../../components/ui";
import { useLoad } from "../shared";

const sel = "h-10 rounded-sm border border-white/15 bg-ink px-3 text-sm text-white";

export function AdjustmentsPanel({ periodId, items, locked, onDone }: { periodId: string; items: Adjustment[]; locked: boolean; onDone: () => void }) {
  const loader = useCallback(() => adminApi.employees(), []);
  const { data: emps } = useLoad(loader);
  const [f, setF] = useState({ employee_id: "", type: "REIMBURSEMENT", amount: "", reason: "" });
  const names = Object.fromEntries((emps ?? []).map((e) => [e.id, e.full_name]));
  const add = async () => {
    try {
      await periodApi.addAdjustment({ employee_id: f.employee_id, period_id: periodId, type: f.type, amount_cents: Math.round(Number(f.amount) * 100), reason: f.reason });
      toast.success("Adjustment created — it needs approval before finalizing");
      setF({ ...f, amount: "", reason: "" }); onDone();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const approve = async (id: string) => {
    const note = window.prompt("Approval note (required)") ?? "";
    if (!note.trim()) return;
    try { await periodApi.approveAdjustment(id, note); toast.success("Adjustment approved"); onDone(); } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <section className="space-y-3" data-testid="adjustments-panel">
      <p className="label-caps text-zinc-400">Adjustments (separate lines, never mixed into hours)</p>
      <ul className="space-y-1 text-xs text-zinc-300">
        {!items.length && <li className="text-zinc-500" data-testid="adjustments-empty">No adjustments in this period.</li>}
        {items.map((a) => (
          <li key={a.id} className="flex flex-wrap items-center gap-3" data-testid={`adjustment-${a.id}`}>
            <span className="text-white">{names[a.employee_id] ?? a.employee_id}</span><span>{a.type}</span><span className="font-mono">{money(a.amount_cents)}</span><span className="text-zinc-500">{a.reason}</span>
            {a.approved_by ? <span className="text-emerald-300" data-testid={`adjustment-approved-${a.id}`}>Approved</span>
              : !locked && <button className="font-semibold text-signal hover:text-white" onClick={() => approve(a.id)} data-testid={`adjustment-approve-${a.id}`}>Approve</button>}
          </li>
        ))}
      </ul>
      {!locked && (
        <div className="flex flex-wrap gap-2">
          <select className={sel} value={f.employee_id} onChange={(e) => setF({ ...f, employee_id: e.target.value })} data-testid="adjustment-employee"><option value="">Employee…</option>{emps?.map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}</select>
          <select className={sel} value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="adjustment-type">{["REIMBURSEMENT", "CORRECTION", "BONUS", "OTHER"].map((t) => <option key={t}>{t}</option>)}</select>
          <TextInput className="w-28" placeholder="± $" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="adjustment-amount" />
          <TextInput className="w-64" placeholder="Reason" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="adjustment-reason" />
          <Button variant="secondary" onClick={add} disabled={!f.employee_id || !f.amount || isNaN(Number(f.amount)) || !f.reason.trim()} data-testid="adjustment-add-btn">Add adjustment</Button>
        </div>
      )}
    </section>
  );
}
